import { useCallback, useEffect, useState } from 'react';
import { fetchLessonPreview } from '../api/client.js';
import type { LessonPreview } from '../types/lessonPreview.js';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; preview: LessonPreview }
  | { status: 'error' };

// Only dedupe concurrent reads: review decisions must use fresh live content on each visit.
const inFlight = new Map<string, Promise<LessonPreview>>();
const disposers = new Set<() => void>();

/** Test-only isolation, including hung requests whose timeout has not fired. */
export function resetLessonPreviewCacheForTests(): void {
  for (const dispose of disposers) dispose();
  disposers.clear();
  inFlight.clear();
}

function loadPreview(id: string): Promise<LessonPreview> {
  const pending = inFlight.get(id);
  if (pending) return pending;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const controller = new AbortController();
  const dispose = () => {
    if (timer !== undefined) clearTimeout(timer);
    controller.abort();
  };
  disposers.add(dispose);
  const request = Promise.race([
    fetchLessonPreview(id, controller.signal),
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Lesson preview timed out'));
      }, 20_000);
    }),
  ]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
    disposers.delete(dispose);
    if (inFlight.get(id) === request) inFlight.delete(id);
  });
  inFlight.set(id, request);
  return request;
}

export function useLessonPreview(workItemId: string): LoadState & { retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const [state, setState] = useState<{ id: string; attempt: number; value: LoadState }>({ id: workItemId, attempt, value: { status: 'loading' } });
  useEffect(() => {
    let cancelled = false;
    setState({ id: workItemId, attempt, value: { status: 'loading' } });
    void loadPreview(workItemId).then(
      (preview) => { if (!cancelled) setState({ id: workItemId, attempt, value: { status: 'ready', preview } }); },
      () => { if (!cancelled) setState({ id: workItemId, attempt, value: { status: 'error' } }); },
    );
    return () => { cancelled = true; };
  }, [workItemId, attempt]);
  return { ...(state.id === workItemId && state.attempt === attempt ? state.value : { status: 'loading' as const }), retry };
}
